// Terms & Conditions content + version constant.
//
// Versioning:
//   - Bump TERMS_VERSION whenever you make a MATERIAL change to the
//     terms (anything affecting user obligations, liability, payments,
//     data handling). Cosmetic copy edits do NOT need a bump.
//   - The server stores the version each user accepted in
//     `terms_acceptances`. On login, if their accepted version != current
//     TERMS_VERSION, the UI surfaces a re-acceptance modal before
//     letting them proceed.
//
// Drafted as senior counsel — covers SaaS terms, payment intermediary
// disclaimer (we are NOT a payment processor for rent payments), data
// handling under DPDP Act 2023, governing law, dispute resolution.
// IT IS STILL CRITICAL to have an Indian advocate review this before
// public launch — wrong language can void key protections.

export const TERMS_VERSION = "1.0";
export const TERMS_EFFECTIVE_DATE = "2026-04-26";

export const COMPANY_NAME = "Rajieev House Rental";
export const COMPANY_JURISDICTION = "Sivakasi, Tamil Nadu, India";

// Section data — rendered as collapsible sections by TermsView.
// Keep paragraphs short — they read better in a mobile modal.
export const TERMS_SECTIONS = [
    {
        id: "intro",
        title: "1. Introduction & Acceptance",
        body: [
            `These Terms and Conditions ("Terms") govern your access to and use of the ${COMPANY_NAME} application, website, and related services (collectively, the "Service"), operated from ${COMPANY_JURISDICTION}.`,
            `By creating an account, accessing, or using the Service, you confirm that you have read, understood, and agreed to be bound by these Terms and our Privacy Policy. If you do not agree, you must not use the Service.`,
            `These Terms apply to landlords, tenants, service-persons, organization staff, and any other user (collectively "you" or "User"). Different sections may apply to you depending on your role.`,
            `You must be at least 18 years of age and competent to enter into a contract under the Indian Contract Act, 1872, to use this Service.`,
        ],
    },
    {
        id: "service",
        title: "2. Nature of the Service",
        body: [
            `The Service is a rental management software-as-a-service (SaaS) platform. It helps landlords manage properties, tenants, leases, and rent collection records, and helps tenants track their tenancy, payments, and maintenance requests.`,
            `IMPORTANT — PAYMENTS: For rent payments between landlord and tenant, the Service is a record-keeping tool only. We do NOT process or hold any rent payment money. UPI, cash, bank-transfer, and cheque payments are made directly between landlord and tenant. We are not a payment intermediary, payment system, or payment aggregator under the Payment and Settlement Systems Act, 2007 for these flows.`,
            `Subscription billing for landlord plans is processed by our payment gateway partner (Razorpay) under their terms. Disputes about subscription charges should follow the gateway's process.`,
            `The Service does not provide legal, financial, taxation, or real-estate advice. The Rent Act guidance, agreement templates, and similar content are informational and not a substitute for advice from a qualified professional.`,
        ],
    },
    {
        id: "account",
        title: "3. Account Registration & Security",
        body: [
            `You agree to provide accurate, current, and complete information during registration and to keep this information up to date. Providing false information may result in immediate account termination.`,
            `You are responsible for maintaining the confidentiality of your password and for all activity under your account. Notify us immediately of any unauthorized access.`,
            `One person may not maintain duplicate accounts in the same role to circumvent plan limits, evade bans, or for any deceptive purpose.`,
            `We may verify your phone number, email, or identity at any time. Suspicious or fraudulent verification attempts will result in account suspension.`,
        ],
    },
    {
        id: "landlord",
        title: "4. Landlord-Specific Obligations",
        body: [
            `If you are a landlord, you represent that you are the lawful owner of, or are otherwise legally entitled to lease, the properties you list on the Service.`,
            `You agree to comply with all applicable rent control laws of your state, including registration requirements, deposit limits, eviction procedures, and tenant rights. The Service does not exempt you from these laws.`,
            `You agree NOT to use the Service to: (a) discriminate against tenants on protected grounds; (b) demand security deposits in excess of legally permissible limits; (c) attempt eviction without due legal process; (d) cut off utilities or change locks to coerce a tenant; (e) collect "black money" or refuse to issue rent receipts.`,
            `You are solely responsible for the accuracy of property listings, rental terms, and tenant communications you create through the Service.`,
            `Subscription fees for landlord plans are non-refundable except where required by law or as expressly stated in our refund policy.`,
        ],
    },
    {
        id: "tenant",
        title: "5. Tenant-Specific Obligations",
        body: [
            `If you are a tenant, you agree to provide accurate identity, employment, and rental-history information when requested by your landlord through the Service.`,
            `You agree to pay rent and other lawful dues on time as per your rental agreement. The Service merely records these payments; it is not a guarantor.`,
            `You acknowledge that records of rent payments, maintenance requests, and chat messages stored on the Service may be used as evidence in tenancy disputes.`,
            `You agree NOT to: (a) damage the rented premises; (b) use the premises for illegal or unauthorized purposes; (c) sublet without your landlord's written consent; (d) impersonate another tenant; (e) submit false maintenance requests.`,
        ],
    },
    {
        id: "service-person",
        title: "6. Service-Person-Specific Obligations",
        body: [
            `If you are a registered service-person (plumber, electrician, etc.), you agree to provide services in a workmanlike manner and to comply with all licensing requirements applicable to your trade.`,
            `Pricing, scope of work, and payment for services rendered are between you and the landlord/tenant. The Service merely facilitates connection and record-keeping.`,
            `You are responsible for any damage caused by your work and any safety violations during the job.`,
        ],
    },
    {
        id: "data",
        title: "7. Data, Privacy & Communications",
        body: [
            `We collect and process personal data (name, phone, address, payment records, ID details where provided) as described in our Privacy Policy, in accordance with the Digital Personal Data Protection Act, 2023.`,
            `By using the Service, you consent to receive transactional communications (push notifications, SMS, WhatsApp, email) related to your account, rent payments, lease updates, and maintenance. You may opt out of marketing communications at any time.`,
            `Aadhaar information: We never store full Aadhaar numbers. Only the last 4 digits are stored for identification purposes, where you provide them. You are not required to share full Aadhaar.`,
            `Data is hosted on secure cloud infrastructure with role-based access control. We do not sell your personal data to third parties.`,
            `On account closure, your personal data may be retained for up to 7 years for legal, tax, and dispute-resolution purposes, then permanently deleted.`,
        ],
    },
    {
        id: "fees",
        title: "8. Fees, Plans & Refunds",
        body: [
            `Tenant and service-person accounts are free. Landlord accounts have free and paid plans. Plan features and prices are described in the Service and may change with prior notice.`,
            `Subscription payments are billed in advance and are non-refundable except (a) as required by law, or (b) where service is fundamentally and materially unavailable due to our fault for extended periods.`,
            `Free trials, when offered, automatically convert to a paid subscription only if you explicitly upgrade. We do not auto-charge after a free trial without your action.`,
            `Taxes (GST) will be charged in addition to displayed prices where applicable.`,
        ],
    },
    {
        id: "ip",
        title: "9. Intellectual Property",
        body: [
            `The Service, including all software, design, content, and trademarks, is owned by ${COMPANY_NAME} and its licensors. You receive a limited, non-exclusive, non-transferable, revocable license to use the Service for its intended purpose.`,
            `You retain ownership of content you upload (property photos, agreements, messages). By uploading, you grant us a worldwide, royalty-free license to host, display, and process this content solely to provide the Service to you.`,
            `You agree not to copy, reverse-engineer, scrape, or build a competing service using our content or APIs.`,
        ],
    },
    {
        id: "prohibited",
        title: "10. Prohibited Uses",
        body: [
            `You must not use the Service to: (a) violate any law or regulation; (b) harass, threaten, defraud, or impersonate any person; (c) post fake listings, fake reviews, or misleading content; (d) attempt to gain unauthorized access to other users' data or our systems; (e) introduce viruses, malware, or scrape data systematically; (f) circumvent rate limits, abuse referral programs, or exploit bugs for personal gain.`,
            `Violation may result in immediate suspension or termination, forfeiture of any unused subscription credit, and reporting to law enforcement where appropriate.`,
        ],
    },
    {
        id: "liability",
        title: "11. Disclaimers & Limitation of Liability",
        body: [
            `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. We do not warrant that the Service will be uninterrupted, error-free, or completely secure.`,
            `We are NOT a party to any rental agreement between landlord and tenant. We are NOT responsible for any dispute, loss, damage, or harm arising from your tenancy, the conduct of any landlord, tenant, service-person, or any third party you interact with through the Service.`,
            `To the maximum extent permitted by law, our total liability to you for any and all claims arising out of or related to the Service shall not exceed the amount you paid us for the Service in the 12 months preceding the claim, or ₹10,000 (Rupees Ten Thousand), whichever is greater.`,
            `We are not liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, including loss of profit, loss of data, or reputational harm.`,
            `Nothing in these Terms excludes liability that cannot be excluded under Indian law (such as for fraud or gross negligence).`,
        ],
    },
    {
        id: "indemnity",
        title: "12. Indemnity",
        body: [
            `You agree to indemnify, defend, and hold harmless ${COMPANY_NAME}, its officers, employees, and partners from any claim, demand, loss, liability, or expense (including reasonable legal fees) arising out of: (a) your breach of these Terms; (b) your violation of any law or third-party right; (c) your use of the Service; (d) any content you post; (e) any tenancy dispute you are a party to.`,
        ],
    },
    {
        id: "termination",
        title: "13. Termination",
        body: [
            `You may close your account at any time from the Settings page. Account closure does not waive accrued obligations (e.g., unpaid rent, unfulfilled maintenance commitments).`,
            `We may suspend or terminate your account, with or without notice, for: (a) violation of these Terms; (b) suspected fraud or illegal activity; (c) non-payment of subscription fees after grace period; (d) inactivity for more than 24 months.`,
            `On termination, your access ends. Some data may be retained per Section 7 (Data) and our Privacy Policy.`,
        ],
    },
    {
        id: "law",
        title: "14. Governing Law & Dispute Resolution",
        body: [
            `These Terms are governed by the laws of India.`,
            `Any dispute arising out of or in connection with these Terms shall first be attempted to be resolved amicably. If not resolved within 30 days, the dispute shall be referred to arbitration by a sole arbitrator under the Arbitration and Conciliation Act, 1996. The seat of arbitration shall be ${COMPANY_JURISDICTION}, language English.`,
            `Subject to the arbitration clause above, the courts at ${COMPANY_JURISDICTION} shall have exclusive jurisdiction.`,
            `Nothing in this section limits a consumer's rights under the Consumer Protection Act, 2019, where it applies.`,
        ],
    },
    {
        id: "changes",
        title: "15. Changes to These Terms",
        body: [
            `We may update these Terms from time to time. Material changes will be notified via in-app notice and you may be required to re-accept the updated Terms before continuing to use the Service.`,
            `The current version is shown at the top of this document. Continuing to use the Service after a material change constitutes acceptance of the new Terms.`,
        ],
    },
    {
        id: "contact",
        title: "16. Contact",
        body: [
            `Questions about these Terms can be sent through the in-app feedback feature or by email to support (provide a real support address before launch).`,
        ],
    },
];

// Convenience: full plain-text rendering for legal record / export.
export function renderTermsAsText() {
    const header = `${COMPANY_NAME} — Terms & Conditions\nVersion ${TERMS_VERSION} • Effective ${TERMS_EFFECTIVE_DATE}\n\n`;
    return header + TERMS_SECTIONS.map(s => `${s.title}\n\n${s.body.join("\n\n")}\n\n`).join("");
}
